import type { MarketFlowData } from '../types/market'
import { formatSectorDisplayName } from '../utils/format'

const defaultApiBaseUrl = 'http://127.0.0.1:8787'

function normalizeApiBaseUrl(value?: string): string {
  return (value?.trim() || defaultApiBaseUrl).replace(/\/+$/, '')
}

export const marketDataApiBaseUrl = normalizeApiBaseUrl(
  import.meta.env.VITE_MARKET_DATA_API_BASE_URL,
)

interface RealSnapshotRow {
  date: string
  time: string
  sectorId: string
  code: string
  name: string
  category: 'industry' | 'concept'
  mainNetFlow: number
}

interface RealMarketResponse {
  date: string
  mode: string
  minuteCount: number
  snapshotCount: number
  snapshots: RealSnapshotRow[]
}

interface RealSectorMetadata {
  sectorId: string
  code: string
  name: string
  category: 'industry' | 'concept'
}

interface RealMarketFrameV2 {
  time: string
  collectedAt: string
  flows: Array<[number, number]>
}

interface RealMarketV2Response {
  version: 2
  date: string
  mode: string
  minuteCount: number
  snapshotCount: number
  sectors: RealSectorMetadata[]
  frames: RealMarketFrameV2[]
}

interface RealIndexRow {
  date: string
  time: string
  marketMinute: string
  code: string
  name: string
  price: number
  change: number
  changePercent: number
  timestamp: number
  collectedAt: string
}

interface RealIndexResponse {
  date: string
  mode: string
  minuteCount: number
  timeline: RealIndexRow[]
}

export interface RealSnapshotResult {
  data: MarketFlowData
  sourceSnapshotCount: number
  mode: 'noon' | 'close'
}

function modeForMinute(minute: string): 'noon' | 'close' {
  return minute <= '11:30' ? 'noon' : 'close'
}

export function adaptRealSnapshot(
  payload: RealMarketResponse,
  date: string,
  minute: string,
  indexPayload: RealIndexResponse | null = null,
): RealSnapshotResult {
  if (payload.date !== date) throw new Error(`真实行情 API 日期不匹配：请求 ${date}，返回 ${payload.date}`)
  const realDate = payload.date
  const mode = modeForMinute(minute)
  if (payload.mode !== mode) throw new Error(`真实行情 API 模式不匹配：请求 ${mode}，返回 ${payload.mode}`)
  const rows = payload.snapshots.filter((snapshot) => snapshot.date === realDate && snapshot.time <= minute)
  if (!rows.some((snapshot) => snapshot.time === minute)) {
    throw new Error(`SQLite 中不存在 ${realDate} ${minute} 的真实快照`)
  }

  const rowsByMinute = new Map<string, RealSnapshotRow[]>()
  for (const row of rows) {
    if (!Number.isFinite(row.mainNetFlow)) throw new Error(`${row.sectorId} 的 f62 无效`)
    const minuteRows = rowsByMinute.get(row.time) ?? []
    minuteRows.push(row)
    rowsByMinute.set(row.time, minuteRows)
  }

  const indexByMinute = new Map<string, RealIndexRow>()
  if (indexPayload?.date === realDate && indexPayload.mode === mode) {
    for (const snapshot of indexPayload.timeline) {
      if (
        snapshot.date === realDate &&
        snapshot.time <= minute &&
        snapshot.code === '000001' &&
        Number.isFinite(snapshot.price) &&
        Number.isFinite(snapshot.change) &&
        Number.isFinite(snapshot.changePercent) &&
        Number.isFinite(snapshot.timestamp)
      ) {
        indexByMinute.set(snapshot.time, snapshot)
      }
    }
  }

  const frames = [...rowsByMinute]
    .sort(([leftTime], [rightTime]) => leftTime.localeCompare(rightTime))
    .map(([time, minuteRows]) => {
      const indexSnapshot = indexByMinute.get(time)
      const ids = new Set<string>()
      for (const row of minuteRows) {
        if (ids.has(row.sectorId)) throw new Error(`${realDate} ${time} 存在重复板块：${row.sectorId}`)
        ids.add(row.sectorId)
      }
      return {
        time,
        index: indexSnapshot
          ? {
              name: indexSnapshot.name,
              price: indexSnapshot.price,
              change: indexSnapshot.change,
              changePercent: indexSnapshot.changePercent,
              timestamp: indexSnapshot.timestamp,
              available: true,
            }
          : {
              name: '上证指数（数据缺失）',
              price: 0,
              change: 0,
              changePercent: 0,
              available: false,
            },
        sectors: minuteRows.map((row) => ({
          id: row.sectorId,
          name: row.name,
          displayName: formatSectorDisplayName(row.name),
          flow: row.mainNetFlow,
        })),
      }
    })

  return {
    sourceSnapshotCount: rows.length,
    mode,
    data: {
      date: realDate,
      frames,
    },
  }
}

export function adaptRealSnapshotV2(
  payload: RealMarketV2Response,
  date: string,
  minute: string,
  indexPayload: RealIndexResponse | null = null,
): RealSnapshotResult {
  const mode = modeForMinute(minute)
  if (payload.version !== 2) throw new Error(`真实行情 v2 版本无效：${String(payload.version)}`)
  if (payload.date !== date) throw new Error(`真实行情 API 日期不匹配：请求 ${date}，返回 ${payload.date}`)
  if (payload.mode !== mode) throw new Error(`真实行情 API 模式不匹配：请求 ${mode}，返回 ${payload.mode}`)
  if (!Array.isArray(payload.sectors)) throw new Error('真实行情 v2 缺少 sectors 元数据')
  if (!Array.isArray(payload.frames)) throw new Error('真实行情 v2 缺少 frames 数据')
  if (payload.minuteCount !== payload.frames.length) {
    throw new Error(`真实行情 v2 分钟数不匹配：声明 ${payload.minuteCount}，实际 ${payload.frames.length}`)
  }

  const metadataIds = new Set<string>()
  for (const sector of payload.sectors) {
    if (
      !sector
      || typeof sector.sectorId !== 'string'
      || typeof sector.code !== 'string'
      || typeof sector.name !== 'string'
      || (sector.category !== 'industry' && sector.category !== 'concept')
    ) {
      throw new Error('真实行情 v2 存在无效 sector 元数据')
    }
    if (metadataIds.has(sector.sectorId)) throw new Error(`真实行情 v2 存在重复 sectorId：${sector.sectorId}`)
    metadataIds.add(sector.sectorId)
  }

  const snapshots: RealSnapshotRow[] = []
  const frameTimes = new Set<string>()
  for (const frame of payload.frames) {
    if (
      !frame
      || typeof frame.time !== 'string'
      || typeof frame.collectedAt !== 'string'
      || !Array.isArray(frame.flows)
      || frame.flows.length === 0
    ) {
      throw new Error('真实行情 v2 存在缺失frame数据')
    }
    if (frameTimes.has(frame.time)) throw new Error(`真实行情 v2 存在重复分钟：${frame.time}`)
    frameTimes.add(frame.time)
    const indexes = new Set<number>()
    for (const flowEntry of frame.flows) {
      if (!Array.isArray(flowEntry) || flowEntry.length !== 2) {
        throw new Error(`${payload.date} ${frame.time} 存在无效flow二元组`)
      }
      const [sectorIndex, mainNetFlow] = flowEntry
      if (!Number.isInteger(sectorIndex) || sectorIndex < 0 || sectorIndex >= payload.sectors.length) {
        throw new Error(`${payload.date} ${frame.time} sector index越界：${String(sectorIndex)}`)
      }
      if (indexes.has(sectorIndex)) {
        throw new Error(`${payload.date} ${frame.time} 存在重复sector index：${sectorIndex}`)
      }
      if (!Number.isFinite(mainNetFlow)) {
        throw new Error(`${payload.date} ${frame.time} sector index ${sectorIndex} 的 flow 无效`)
      }
      indexes.add(sectorIndex)
      const metadata = payload.sectors[sectorIndex]
      snapshots.push({
        date: payload.date,
        time: frame.time,
        sectorId: metadata.sectorId,
        code: metadata.code,
        name: metadata.name,
        category: metadata.category,
        mainNetFlow,
      })
    }
  }
  if (payload.snapshotCount !== snapshots.length) {
    throw new Error(`真实行情 v2 快照数不匹配：声明 ${payload.snapshotCount}，实际 ${snapshots.length}`)
  }

  return adaptRealSnapshot({
    date: payload.date,
    mode: payload.mode,
    minuteCount: payload.minuteCount,
    snapshotCount: payload.snapshotCount,
    snapshots,
  }, date, minute, indexPayload)
}

export async function loadLatestRealSnapshot(
  apiBaseUrl = marketDataApiBaseUrl,
): Promise<RealSnapshotResult> {
  const baseUrl = normalizeApiBaseUrl(apiBaseUrl)
  const response = await fetch(`${baseUrl}/api/collector/status`)
  if (!response.ok) throw new Error(`Collector 状态加载失败：${response.status}`)
  const payload = await response.json() as { lastSuccessfulMinute?: string | null }
  const match = payload.lastSuccessfulMinute?.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/)
  if (!match) throw new Error('Collector 尚无可用的真实分钟快照')
  return loadRealSnapshot(match[1], match[2], baseUrl)
}

export async function loadRealSnapshot(
  date: string,
  minute: string,
  apiBaseUrl = marketDataApiBaseUrl,
): Promise<RealSnapshotResult> {
  const baseUrl = normalizeApiBaseUrl(apiBaseUrl)
  const mode = modeForMinute(minute)
  const [v2Response, indexResponse] = await Promise.all([
    fetch(`${baseUrl}/api/v2/market/${encodeURIComponent(date)}/${mode}`),
    fetch(`${baseUrl}/api/index/${encodeURIComponent(date)}/${mode}`).catch(() => null),
  ])
  const useLegacyResponse = v2Response.status === 404
  const response = useLegacyResponse
    ? await fetch(`${baseUrl}/api/market/${encodeURIComponent(date)}/${mode}`)
    : v2Response
  if (!response.ok) throw new Error(`真实行情 API 加载失败：${response.status}`)
  const indexPayload = indexResponse?.ok
    ? await indexResponse.json() as RealIndexResponse
    : null
  return useLegacyResponse
    ? adaptRealSnapshot(await response.json() as RealMarketResponse, date, minute, indexPayload)
    : adaptRealSnapshotV2(await response.json() as RealMarketV2Response, date, minute, indexPayload)
}
